<script setup>
import { useStore } from "vuex";
import { computed, onMounted, ref, watch } from "vue";
import { getImageInfo } from "../utils/preload-image";

const props = defineProps({
  preview: {
    required: true,
    type: Object
  },
  defaultImageUrl: {
    required: true,
    type: String
  },
  productId: {
    required: true,
    type: Number
  },
  compositionId: {
    required: true,
    type: Number
  }
});

const cachedPreviews = new Map();
const store = useStore();
const artworkSize = computed(() => store.getters["artwork/artworkSize"]);
const artworkUrl = computed(() => store.getters["artwork/artworkUrl"]);
const surface = computed(() => store.getters["artwork/surface"]);
const printableAreas = computed(() => store.getters["creation/printableAreas"]);

const canvasPreview = ref(props.defaultImageUrl);
const previewUrl = computed(() =>
  props.preview.preview_awaited
    ? canvasPreview.value
    : props.preview.preview_url || props.defaultImageUrl
);

const createPreview = async area => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const { image: backdropImage, width, height } = await getImageInfo(area.backdropUrl);
  const { image: artworkImage } = await getImageInfo(area.artworkUrl);

  ctx.canvas.width = width;
  ctx.canvas.height = height;
  ctx.clearRect(0, 0, width, height); // clear canvas
  ctx.drawImage(backdropImage, 0, 0, width, height);

  const angle = (area.rotation * Math.PI) / 180;
  const [x, y] = area.center;
  ctx.save();
  ctx.translate(x * -angle, y * angle);
  ctx.rotate(-angle);
  ctx.drawImage(artworkImage, x - area.width / 2, y, area.width, area.height);
  ctx.restore();
  return canvas.toDataURL();
};

const initPreview = async () => {
  const payload = { productId: props.productId, compositionId: props.compositionId };
  const area = await store.dispatch("creation/fetchAreas", payload);
  const key = JSON.stringify({ ...area, ...payload });

  if (cachedPreviews.has(key)) {
    canvasPreview.value = cachedPreviews.get(key);
    return;
  }

  canvasPreview.value = await createPreview(area);
  cachedPreviews.set(key, canvasPreview.value);
};

watch([artworkSize, artworkUrl, surface, printableAreas], initPreview);
onMounted(initPreview);
</script>

<template>
  <img alt="Product Preview Image" :src="previewUrl" />
</template>
